import {
  ADD_ENROLMENT,
  CLEAR_DRAFT,
  CONFIRM_DRAFT_FAILED,
  CONFIRM_DRAFT_STARTED,
  CONFIRM_DRAFT_SUCCEEDED,
  DISMISS_CONFIRMATION,
  DISMISS_SUBMIT_FAILURE,
  REMOVE_ENROLMENT,
  TOGGLE_UNENROLMENT,
  type EnrolmentDraftAction,
  type PendingEnrolment,
  type PendingEnrolmentKey,
} from './actions'
import { describeSubmitErrors, type SubmitError } from './confirmation'
import { success } from 'utilities/actions'
import { API_ENROLMENT_ENTITLEMENTS_INDEX } from 'state/enrolmentEntitlements/actions'
import { API_PARTICIPANTS_INDEX } from 'state/participants/actions'
import type { EnrolmentEntitlement, Participant } from 'types'

export type EnrolmentDraftState = {
  unenrolments: Record<string, true>
  enrolments: Record<string, PendingEnrolment>
  scope: string | null
  submitting: boolean
  submitFailed: boolean
  submitStatus: number | null
  submitErrors: SubmitError[]
  // Set once a submit succeeds, until the success banner is dismissed.
  confirmed: boolean
}

export type State = EnrolmentDraftState

export const initialState: State = {
  unenrolments: {},
  enrolments: {},
  scope: null,
  submitting: false,
  submitFailed: false,
  submitStatus: null,
  submitErrors: [],
  confirmed: false,
}

const withoutEnrolment = (
  unenrolments: Record<string, true>,
  enrolmentId: string,
): Record<string, true> => {
  const remaining = { ...unenrolments }
  delete remaining[enrolmentId]
  return remaining
}

const toggle = (
  unenrolments: Record<string, true>,
  enrolmentId: string,
): Record<string, true> => {
  if (unenrolments[enrolmentId]) {
    return withoutEnrolment(unenrolments, enrolmentId)
  }
  return { ...unenrolments, [enrolmentId]: true }
}

const pendingEnrolmentKey = ({
  entitlementId,
  participantId,
}: PendingEnrolmentKey): string => `${entitlementId}:${participantId}`

const withEnrolment = (
  enrolments: Record<string, PendingEnrolment>,
  enrolment: PendingEnrolment,
): Record<string, PendingEnrolment> => ({
  ...enrolments,
  [pendingEnrolmentKey(enrolment)]: enrolment,
})

const withoutPendingEnrolment = (
  enrolments: Record<string, PendingEnrolment>,
  enrolment: PendingEnrolmentKey,
): Record<string, PendingEnrolment> => {
  const remaining = { ...enrolments }
  delete remaining[pendingEnrolmentKey(enrolment)]
  return remaining
}

// Only runs on a completed participants load, which covers every page, so a
// participant missing from it really is gone rather than on a later page. A
// participant moved to another account can no longer use the entitlement they
// were added to, so that addition goes too.
const withListedParticipants = (
  enrolments: Record<string, PendingEnrolment>,
  participants: Participant[],
): Record<string, PendingEnrolment> => {
  const activeAccounts = new Map(
    participants
      .filter(participant => participant.active)
      .map(participant => [participant.id, participant.participantGroup.id]),
  )
  const kept: Record<string, PendingEnrolment> = {}
  Object.entries(enrolments).forEach(([key, enrolment]) => {
    if (activeAccounts.get(enrolment.participantId) === enrolment.organisationId) {
      kept[key] = enrolment
    }
  })
  return kept
}

const PARTICIPANTS_LOADED = success(API_PARTICIPANTS_INDEX)

type ParticipantsLoadedAction = {
  type: typeof PARTICIPANTS_LOADED;
  data: Participant[];
}

// Only runs once every page of entitlements has loaded, so an entitlement or
// enrolment missing from it really is gone. Without this, a change to one would
// still be counted but have nothing to show on the page or the review.
const withListedEntitlements = (
  state: State,
  entitlements: EnrolmentEntitlement[],
): State => {
  const enrolmentIds = new Set<string>()
  const enrolledParticipants = new Map<string, Set<string>>()
  entitlements.forEach(entitlement => {
    const participantIds = new Set<string>()
    entitlement.enrolments.forEach(enrolment => {
      enrolmentIds.add(enrolment.id)
      participantIds.add(enrolment.participant.id)
    })
    enrolledParticipants.set(entitlement.id, participantIds)
  })

  const unenrolments: Record<string, true> = {}
  Object.keys(state.unenrolments).forEach(enrolmentId => {
    if (enrolmentIds.has(enrolmentId)) unenrolments[enrolmentId] = true
  })

  const enrolments: Record<string, PendingEnrolment> = {}
  Object.entries(state.enrolments).forEach(([key, enrolment]) => {
    const enrolled = enrolledParticipants.get(enrolment.entitlementId)
    if (enrolled && !enrolled.has(enrolment.participantId)) {
      enrolments[key] = enrolment
    }
  })

  return { ...state, unenrolments, enrolments }
}

const ENTITLEMENTS_LOADED = success(API_ENROLMENT_ENTITLEMENTS_INDEX)

type EntitlementsLoadedAction = {
  type: typeof ENTITLEMENTS_LOADED;
  data: EnrolmentEntitlement[];
}

const hasNoChanges = (state: State): boolean =>
  Object.keys(state.unenrolments).length === 0 &&
  Object.keys(state.enrolments).length === 0

const scopeAfterChange = (state: State, currentScope: string): string =>
  hasNoChanges(state) || state.scope === null ? currentScope : state.scope

// A failure lasts only as long as the review it was shown in: cleared by a new
// submit, or by closing the review. So it never outlives the draft it describes.
const withoutSubmitFailure = {
  submitFailed: false,
  submitStatus: null,
  submitErrors: [],
}

const statusCode = (status: string | undefined): number | null =>
  status === undefined ? null : Number(status)

const reducer = (
  state: State = initialState,
  action:
    | EnrolmentDraftAction
    | ParticipantsLoadedAction
    | EntitlementsLoadedAction,
): State => {
  switch (action.type) {
    case TOGGLE_UNENROLMENT:
      return {
        ...state,
        unenrolments: toggle(state.unenrolments, action.enrolmentId),
        scope: scopeAfterChange(state, action.scope),
      }
    case ADD_ENROLMENT:
      return {
        ...state,
        enrolments: withEnrolment(state.enrolments, action.enrolment),
        scope: scopeAfterChange(state, action.scope),
      }
    case REMOVE_ENROLMENT:
      return {
        ...state,
        enrolments: withoutPendingEnrolment(
          state.enrolments,
          action.enrolment,
        ),
      }
    case PARTICIPANTS_LOADED:
      return {
        ...state,
        enrolments: withListedParticipants(state.enrolments, action.data),
      }
    case ENTITLEMENTS_LOADED:
      return withListedEntitlements(state, action.data)
    case CONFIRM_DRAFT_STARTED:
      return {
        ...state,
        submitting: true,
        ...withoutSubmitFailure,
      }
    case CONFIRM_DRAFT_SUCCEEDED:
      return { ...initialState, confirmed: true }
    case CONFIRM_DRAFT_FAILED:
      return {
        ...state,
        submitting: false,
        submitFailed: true,
        submitStatus: statusCode(action.status),
        submitErrors: describeSubmitErrors(
          action.context.request,
          action.errors || [],
        ),
      }
    case DISMISS_CONFIRMATION:
      return { ...state, confirmed: false }
    case DISMISS_SUBMIT_FAILURE:
      return { ...state, ...withoutSubmitFailure }
    case CLEAR_DRAFT:
      return initialState
    default:
      return state
  }
}

export default reducer
